import type { MedioPagoReserva } from '@padel/domain';
import { nombreDeMedioReserva } from '@/publico/dominio/formato';
/**
 * Pantalla de reserva confirmada.
 *
 * ## Que se muestra y que no
 *
 * Se muestra lo que la persona necesita: cancha, día, horario, duración, precio y método
 * de pago. La cancelación se gestiona comunicándose con el complejo.
 *
 * No se muestra ningun identificador interno: ni `reservaId` ni el numero de mostrador,
 * que ni siquiera viaja por este canal (docs/MODELO_DATOS.md §13). Tampoco se repiten la
 * cedula ni el telefono: ya se enviaron, no hacen falta en pantalla y cada lugar donde
 * aparece un dato personal es un lugar del que puede filtrarse.
 *
 * Un reintento con la misma clave devuelve la misma reserva sin duplicarla.
 */

import { BrandLogo } from '@/componentes/BrandLogo';
import type { ReservaCreada } from '@/infra/contratos';
import { DatosTransferenciaConfirmada } from '@/publico/componentes/InformacionTransferencia';
import * as formato from '@/publico/dominio/formato';
import { finDeTurno } from '@/publico/dominio/horario';
import type { DuracionMinutos } from '@padel/domain';

interface PropiedadesConfirmacion {
  readonly reserva: ReservaCreada;
  readonly canchaNombre: string;
  readonly duracionMinutos: DuracionMinutos;
  readonly medioPago: MedioPagoReserva;
  readonly alEmpezarOtra: () => void;
}

export function Confirmacion({
  reserva,
  canchaNombre,
  duracionMinutos,
  medioPago,
  alEmpezarOtra,
}: PropiedadesConfirmacion): React.JSX.Element {
  const fin = finDeTurno(reserva.inicioUtc, duracionMinutos);

  return (
    <section aria-labelledby="confirmacion-titulo" className="confirmacion">
      <div className="confirmacion__marca">
        <BrandLogo tamano="compacta" />
      </div>

      <h2 className="confirmacion__titulo" id="confirmacion-titulo">
        ¡Listo! Tu cancha esta reservada
      </h2>

      <p className="confirmacion__bajada">
        Te esperamos en {canchaNombre} el {formato.fechaLarga(reserva.inicioLocalFecha)} a las{' '}
        {reserva.inicioLocalHora}.
      </p>

      <dl className="resumen__datos">
        <div className="resumen__fila">
          <dt>Cancha</dt>
          <dd>{canchaNombre}</dd>
        </div>
        <div className="resumen__fila">
          <dt>Dia</dt>
          <dd>{formato.fechaLarga(reserva.inicioLocalFecha)}</dd>
        </div>
        <div className="resumen__fila">
          <dt>Horario</dt>
          <dd>
            {reserva.inicioLocalHora} a {fin === null ? '—' : fin.localHora}
            {reserva.cruzaMedianoche && fin !== null
              ? ` del ${formato.fechaLarga(fin.localFecha)}`
              : ''}
          </dd>
        </div>
        <div className="resumen__fila">
          <dt>Duracion</dt>
          <dd>{formato.duracion(duracionMinutos)}</dd>
        </div>
        <div className="resumen__fila resumen__fila--precio">
          <dt>Precio</dt>
          <dd>{formato.precio(reserva.precioCentesimos)}</dd>
        </div>
        <div className="resumen__fila">
          <dt>Método de pago</dt>
          <dd>{nombreDeMedioReserva(medioPago)}</dd>
        </div>
      </dl>

      {(medioPago === 'transferencia' || medioPago === 'combinado') &&
      reserva.datosTransferencia !== undefined ? (
        <DatosTransferenciaConfirmada datos={reserva.datosTransferencia} />
      ) : null}

      <p className="nota">Para cancelar la reserva, comunicate con el complejo.</p>

      <div className="acciones">
        <button className="boton boton--primario" onClick={alEmpezarOtra} type="button">
          Hacer otra reserva
        </button>
      </div>
    </section>
  );
}
